import { useEffect, type ReactElement, type ReactNode } from 'react';
import { useAngularContext } from 'AngularContext';
import { useRequiredCurrentUser } from 'FrontRoyalAngular';
import { type ReactRouterBridge } from 'Navigation/angularModule/scripts/react_router_bridge';
import { TuitionAndRegistrationSection, getTuitionAndRegistrationSection } from 'TuitionAndRegistrationSection';
import { getAoiRecord, getCompletedStripeCheckoutSession, setCompletedStripeCheckoutSession } from 'Users';
import { getApplicationStatusMessages } from '../getApplicationStatusMessages';
import { useAcceptedApplicationModal } from '../useAcceptedApplicationModal';
import { CohortRegistrationFullScholarship } from '../../tuition_and_registration/components/CohortRegistrationFullScholarship';
import { CohortRegistrationNoPlanSelection } from '../../tuition_and_registration/components/CohortRegistrationNoPlanSelection';
import { CohortRegistrationPlanSelection } from '../../tuition_and_registration/components/CohortRegistrationPlanSelection';
import { Divider } from '../../settingsShared/Divider';
import { AcceptedMessage } from './AcceptedMessage';
import { PaidProgramWelcomePackage } from './PaidProgramWelcomePackage';
import { RegisteredManageBilling } from './RegisteredManageBilling';

const COMPLETED_CHECKOUT_PARAM = 'completed_stripe_checkout_session';

const INSET = 'px-4 sm:px-10';

export function ApplicationStatus(): ReactElement | null {
    const currentUser = useRequiredCurrentUser();
    const $injector = useAngularContext();
    const messages = getApplicationStatusMessages(currentUser);
    useAcceptedApplicationModal(currentUser);

    // A return from Stripe Checkout arrives before the webhooks create the TuitionContract. The flag covers that gap.
    // react2Angular mounts this without a Router, so read the route through reactRouterBridge.
    const bridge = $injector?.get<ReactRouterBridge>('reactRouterBridge');
    // search() with no arguments returns the params record; with arguments it returns the bridge.
    const searchParams = bridge?.search() as Record<string, string> | undefined;
    const completedCheckout = searchParams?.[COMPLETED_CHECKOUT_PARAM] === 'true';
    useEffect(() => {
        if (!completedCheckout || !bridge) return;
        setCompletedStripeCheckoutSession(currentUser, true);
        bridge.search(COMPLETED_CHECKOUT_PARAM, null);
    }, [completedCheckout, bridge, currentUser]);

    // Be defensive: this can render briefly if someone navigates straight here with no AOI record.
    if (!getAoiRecord(currentUser)) return null;

    // The vanilla CurrentUser has no completedStripeCheckoutSession, so supply it. Read the param too,
    // because the effect writes the stored flag after this render.
    const section = getTuitionAndRegistrationSection({
        ...currentUser,
        completedStripeCheckoutSession: completedCheckout || getCompletedStripeCheckoutSession(currentUser),
    });

    /*
        `section` is null when the user's cohort and application status mean they do not need to
        register. `empty` and `specialCase` mean they do need to register, but there is nothing to
        show in the body -- the header and subheader still talk about registration.
    */
    const showTuitionAndRegistrationBody =
        !!section &&
        ![TuitionAndRegistrationSection.empty, TuitionAndRegistrationSection.specialCase].includes(section);

    /*
        This section supplies its own vertical padding, the way the other React sections do:
        application_status is in `reactBasedSections`, which puts `no-padding-bottom` on .main-box.
    */
    return (
        <div className={`pb-7.5 pt-5 ${INSET}`}>
            {!showTuitionAndRegistrationBody && messages.acceptedPrimary ? (
                <AcceptedMessage message={messages.acceptedPrimary} />
            ) : null}

            {showTuitionAndRegistrationBody ? registrationScreenFor(section) : null}
        </div>
    );
}

function WithWelcomePackage({ children }: { children: ReactNode }): ReactElement {
    return (
        <>
            {children}
            <div className="pt-5">
                <Divider />
            </div>
            <PaidProgramWelcomePackage />
        </>
    );
}

function registrationScreenFor(section: TuitionAndRegistrationSection): ReactElement | null {
    switch (section) {
        case TuitionAndRegistrationSection.cohortRegistrationNoPlanSelection:
            return (
                <WithWelcomePackage>
                    <CohortRegistrationNoPlanSelection />
                </WithWelcomePackage>
            );
        case TuitionAndRegistrationSection.cohortRegistrationFullScholarship:
            return (
                <WithWelcomePackage>
                    <CohortRegistrationFullScholarship />
                </WithWelcomePackage>
            );
        case TuitionAndRegistrationSection.cohortRegistrationPlanSelection:
            return (
                <WithWelcomePackage>
                    <CohortRegistrationPlanSelection />
                </WithWelcomePackage>
            );
        case TuitionAndRegistrationSection.registeredManageBilling:
            return <RegisteredManageBilling />;
        default:
            return null;
    }
}
